<template>
  <div class="credit-card">
    <img class="header" :src="require('@/assets/subscription/subscribe_header.svg')">

    <div class="top-section">
      <div class="title">היי {{ this.householdName }}, איזה כיף שממשיכים לצמוח יחד!</div>
      <benefit-notification-box class="benefit-notification-wrapper"/>
    </div>
    <div class="separator"/>
    <div class="personal-info">
      <div class="title">פרטי בעל הכרטיס</div>
      <personal-info-form/>
    </div>
    <div class="separator"/>
    <div class="credit-card-details">
      <credit-card-details-form @init-failed="creditCardFormInitFailed"/>
      <check-card-promo-eligibility v-if="showCheckDiscountEligibility" :createBenefit="createDiscountBenefitIfNeeded"/>
    </div>
    <div class="separator"></div>
    <div class="summary">
      <div class="payment-details">
        <div class="amount-wrapper">
          <div class="amount-title">סה״כ בחודש</div>
          <div class="amount">45 ₪</div>
        </div>
        <div class="billing-date-wrapper">
          <div class="billing-date-title">תאריך ראשון לחיוב</div>
          <free-months-billing-date v-if="benefitType === BENEFIT_TYPES.FREE_MONTHS" :first-billing-date="firstBillingDate"/>
          <div v-else class="billing-date">{{ formattedBillingDate }}</div>
        </div>
      </div>
      <div class="button-container" v-if="!tokenizationError">
        <riseup-button title="לתשלום מאובטח" :size="'small'" :action="onPaymentClicked" :loading="loadingToken"
                       :disabled="loadingToken || !validPersonalInfo || subscriptionButtonClicked"/>
      </div>
      <div class="result-container" v-else>
        <div class="top">
          <div class="icon">
            <img class="icon" :src="require('@/assets/icons/alert-icon.svg')">
          </div>
          <div class="message">
            לא הצלחנו לחייב את הכרטיס
          </div>
        </div>
        <div class="cta">
          אפשר לרענן את העמוד ולנסות להשתמש בכרטיס אחר.
        </div>
      </div>
      <div class="link" v-on:click="() => backToCashflow()">חזרה לתזרים שלך</div>
    </div>
  </div>
</template>

<script>
import BaseUI from 'base-ui';
import { mapState, mapGetters, mapActions } from 'vuex';
import moment from 'moment';

import BenefitNotificationBox
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/components/BenefitNotificationBox';
import router from '@/router';
import FreeMonthsBillingDate
  from '@/pages/responsive-pages/authenticated/subscription/components/FreeMonthsBillingDate';
import promotionConsts from '@/constants/promotions';
import PersonalInfoForm from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/components/PersonalInfoForm';
import PaymentHelper from '@/pages/responsive-pages/authenticated/payment/PaymentHelper';
import MetricsApi from '@/api/MetricsApi';
import WithFirstBillingDate from '@/pages/responsive-pages/authenticated/subscription/WithFirstBillingDate';
import Segment from '../../../../../Segment';
import CreditCardDetailsForm from '../../payment/CreditCardDetailsForm.vue';
import PaymeInterface from '../../../../../Payme';
import CheckCardPromoEligibility from './CheckCardPromoEligibility.vue';

export default {
  name: 'CreditCard',
  components: {
    CreditCardDetailsForm,
    RiseupButton: BaseUI.RiseupButton,
    PersonalInfoForm,
    BenefitNotificationBox,
    CheckCardPromoEligibility,
    FreeMonthsBillingDate,
  },
  mixins: [PaymentHelper, WithFirstBillingDate],
  async created() {
    Segment.trackUserGot('SubscriptionCreditCardEntered');
    await this.createDiscountBenefitIfNeeded();
  },
  destroyed() {
    PaymeInterface.teardown();
  },
  data() {
    return {
      subscriptionButtonClicked: false,
    };
  },
  computed: {
    ...mapState('subscription', ['tokenizationError', 'loadingToken']),
    ...mapState('discountPromotion', ['eligibleForDiscountPromotion']),
    ...mapGetters('subscription', ['validPersonalInfo']),
    ...mapState('session', ['customerId']),
    ...mapGetters('session', ['householdName']),
    ...mapState('benefits', ['benefitId', 'benefitType', 'benefitAmount', 'benefitPromotion']),
    ...mapGetters('benefits', ['BENEFIT_TYPES']),
    formattedBillingDate() {
      return moment(this.firstBillingDate).format('D.M.YY');
    },
    showCheckDiscountEligibility() {
      return !this.benefitId || this.benefitPromotion === promotionConsts.PROMOTION_NAMES.DISCOUNT_TWO_MONTHS_FREE;
    },
  },
  methods: {
    ...mapActions('subscription', ['createSubscription', 'fetchFirstBillingDate']),
    ...mapActions('benefits', ['redeemBenefit', 'createBenefit']),
    backToCashflow() {
      router.push({ path: '/sr' });
    },
    async createDiscountBenefitIfNeeded() {
      if (this.eligibleForDiscountPromotion && !this.benefitId) {
        await this.createBenefit(promotionConsts.PROMOTION_NAMES.DISCOUNT_TWO_MONTHS_FREE);
        Segment.trackUserGot('DiscountFirstMonthFreeBenefitCreated');
        await this.fetchFirstBillingDate();
      }
    },
    async onPaymentClicked() {
      Segment.trackUserGot('CreditCardPaymentClicked');
      this.subscriptionButtonClicked = true;
      try {
        MetricsApi.sendCounter('subscription.paymentButton.clicked', { type: 'new' });
        const subscriptionResult = await this.createSubscription(this.customerId);
        if (subscriptionResult && this.benefitId) {
          await this.redeemBenefit();
        }
      } finally {
        this.subscriptionButtonClicked = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~base-ui/src/scss/riseup-colors';
  @import '../../../../../scss/mixins';

  .credit-card {
    .header {
      width: 100%;
    }

    .separator {
      height: 1px;
      width: 100%;
      background: $riseup_gray_0;
    }

    .top-section {
      @include responsive-padding;
      margin-top: 30px;
      margin-bottom: 45px;

      .title {
        font-family: SimplerPro;
        font-weight: bold;
        line-height: 1.17;
        font-size: 22px;
        margin-bottom: 30px;
      }
    }

    .personal-info {
      @include responsive-padding;
      margin-top: 30px;
      margin-bottom: 30px;
      font-family: SimplerPro;

      .title {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 20px;
      }
    }

    .credit-card-details {
      @include responsive-padding;
      margin-top: 30px;
      margin-bottom: 20px;
    }

    .summary {
      @include responsive-padding;
      margin-top: 20px;
      margin-bottom: 30px;

      .check-card-promo-eligibility {
        margin-top: 10px;
      }

      .payment-details {
        display: flex;
        width: 100%;
        margin-bottom: 20px;

        .amount-wrapper {
          flex-grow: 1;
          .amount-title {
            padding-top: 10px;
            font-size: 14px;
            line-height: 1;
          }

          .amount {
            font-size: 20px;
            font-weight: bold;
            padding: 11px 0 12px;
            line-height: 1;
          }
        }

        .billing-date-wrapper {
          flex-grow: 1;
          .billing-date-title {
            padding-top: 10px;
            font-size: 14px;
            line-height: 1;
          }

          .billing-date {
            font-size: 20px;
            font-weight: bold;
            padding: 11px 0 12px;
            line-height: 1;
          }
        }
      }

      .link {
        padding: 11px 0 34px 0;
        cursor: pointer;
        text-decoration: underline;
        color: $riseup_blue;
        text-align: center;
        font-weight: bold;
      }

      .button-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 20px;

        .riseup-button {
          width: 100%;
        }
      }

      .result-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 18px 16px;
        border-radius: 3px;
        background-color: rgba($riseup_gray_0, 0.4);

        .top {
          display: flex;
          flex-direction: row;
          align-items: center;
          font-family: SimplerProBold;
          font-size: 20px;
          color: $riseup_danger_red;
          margin-left: 20px;

          .icon {
            margin-left: 11px;
          }

          .message {
            font-size: 20px;
            font-family: SimplerProBold;
            color: $riseup_danger_red;
          }
        }

        .cta {
          margin-top: 5px;
          font-family: SimplerPro;
          font-size: 16px;
          line-height: 1.5;
        }
      }
    }
  }
</style>
